import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, Subscription } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { ApiErrorHandlerService } from '../../services/api-error-handler/api-error-handler.service';
import { TSettings } from '../../services/appconfig/settings.model';
import { OrderProcessingService } from '../../services/order-processing/order-processing.service';
import { PhoneNumberService } from '../../services/phone-number/phone-number.service';
import { SettingsService } from '../../services/settings/settings.service';
import { submitAccessCode } from '../../utils/access-code-submit';
import {
  ORDER_ID_TYPE,
  RETAIL_PATHS,
  SCAN_TYPE,
  TOAST_TYPES,
  VIEW_TYPES,
} from '../../utils/constants';
import { Utils } from '../../utils/utils';

@Component({
  selector: 'frodo-numeric-keyboard',
  templateUrl: './numeric-keyboard.component.html',
  styleUrls: ['./numeric-keyboard.component.scss'],
})
export class NumericKeyboardComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() viewType;
  @Input() side;
  @Input() countryDialCode: string;
  @Input() phoneNumberLength: number;
  @Input() globalIsLoading = false;
  @Input() disableEnter = false;
  @Input() preventSubmit?: () => boolean;
  @Output() valueSubmit: EventEmitter<string> = new EventEmitter();
  @Output() codeSubmitted: EventEmitter<string> = new EventEmitter();

  flow = '';
  isLoading = false;
  expireLoaded: boolean;
  type = '';
  countryIsoCode: string;
  deviceType: string;
  subscription: Subscription = new Subscription();
  keydownHandler: (event: KeyboardEvent) => void;
  state: RouterStateSnapshot;
  hdSettings$: Observable<Partial<TSettings>>;
  currentBoxInput: string;
  minLength = 0;
  maxLength = 0;
  inputs = {};
  code = '';
  showBoxes = false;
  isPassword = false;
  badPhoneNumberMessage = 'common.badPhoneNumberMsg';
  noPhoneNumberMessage = 'common.noPhoneNumberMsg';
  enableDeliveryProvider = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private phoneNumberService: PhoneNumberService,
    private router: Router,
    private utils: Utils,
    private settingsService: SettingsService,
    private orderProcessingService: OrderProcessingService,
    private apiErrorHandlerService: ApiErrorHandlerService,
    private translateService: TranslateService
  ) {
    this.state = this.router.routerState.snapshot;
    this.hdSettings$ = this.settingsService.getSettings;
  }

  ngOnInit(): void {
    localStorage.removeItem('scheduleId');
    localStorage.removeItem('dispenseId');
    localStorage.removeItem('schedule');
    this.type = this.activatedRoute.snapshot.paramMap.get('type');
    this.flow = this.activatedRoute.snapshot.paramMap.get('flow');
    this.subscription.add(
      this.hdSettings$.subscribe((response) => {
        this.expireLoaded = response['defaultSettings']['expireLoaded'];
        this.deviceType = response['device']['deviceType'];
        this.countryIsoCode = response.apps.frodo.homescreen.country;
        this.minLength = response.accessCode.length.minimum;
        this.maxLength = this.countryDialCode
          ? this.phoneNumberLength
          : response.accessCode.length.maximum;
        this.showBoxes = this.minLength === this.maxLength;
        this.enableDeliveryProvider = response.apps.frodo.enableDeliveryProvider;
      })
    );
    this.subscription.add(
      this.utils.getLoadingValue().subscribe((value) => {
        this.isLoading = value;
      })
    );

    this.isPassword = ['password', 'associate-id'].some((substring) =>
      this.state.url.includes(substring)
    );

    this.addPhysicalNumericKeyboardListener();
  }

  initInputBoxesValues(): void {
    const codeInputId = 'code';
    const code = document.getElementById(codeInputId);
    const firstBoxId = 'input0';
    const boxInput = document.getElementById(firstBoxId);
    if (boxInput) {
      for (let index = 0; index < this.maxLength; index++) {
        const inputId = `input${index}`;
        this.inputs[inputId] = '';
        document.getElementById(inputId)['value'] = '';
      }
      this.setFocus(firstBoxId);
    } else if (code) {
      code['value'] = '';
      this.setFocus(codeInputId);
    }
  }

  ngAfterViewInit() {
    this.initInputBoxesValues();
  }

  onInputFocus(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.currentBoxInput = inputElement.id;
  }

  showLengthRestrictionInputMsg(): void {
    const msg = this.translateService.instant('common.codeLengthRestrictionInputMsg', {
      min: this.minLength,
      max: this.maxLength,
    });
    this.apiErrorHandlerService.showMessage(msg, TOAST_TYPES.Error);
  }

  showLengthRestrictionBoxesMsg(): void {
    const msg = this.translateService.instant('common.codeLengthRestrictionBoxesMsg', {
      max: this.maxLength,
    });
    this.apiErrorHandlerService.showMessage(msg, TOAST_TYPES.Error);
  }

  setValue(event): void {
    const selector = document.getElementById('code');
    const boxInput: HTMLElement = document.getElementById(this.currentBoxInput);
    const digit = event.target.textContent;

    if (boxInput) boxInput.dispatchEvent(new Event('focusout'));

    if (this.isPassword) {
      this.appendDigitToCodeInput(digit);
      return;
    }
    if (selector && selector['value'].length >= this.maxLength) {
      return this.showLengthRestrictionInputMsg();
    }
    if (this.getInputBoxesValues().length >= this.maxLength) {
      return this.showLengthRestrictionBoxesMsg();
    }
    if (selector && selector['value'].length < this.maxLength) {
      this.appendDigitToCodeInput(digit);
      return;
    }
    if (boxInput && boxInput['value'].length < this.maxLength) {
      return this.setBoxesValues(boxInput, digit);
    }
  }

  setBoxesValues(boxInput: HTMLElement, value: string): void {
    if (boxInput['value'].length > 0) return;
    this.inputs[this.currentBoxInput] = value;
    boxInput['value'] = value;
    boxInput.dispatchEvent(new Event('input'));
  }

  private appendDigitToCodeInput(digit: string): void {
    const selector = document.getElementById('code');
    if (!selector) return;

    const currentValue = selector['value'];
    selector['value'] = currentValue ? `${currentValue}${digit}` : digit;
  }

  setFocus(id: string): void {
    this.currentBoxInput = id;
    document.getElementById(id).focus();
  }

  delete(): void {
    const selector = document.getElementById('code');
    if (selector) {
      const currentValue = selector['value'];
      const newValue = currentValue.slice(0, -1);
      selector['value'] = newValue;
    } else {
      this.removeBoxesValues();
    }
  }

  removeBoxesValues(): void {
    const currentInput = document.getElementById(this.currentBoxInput);
    const currentValue = currentInput['value'];
    const index = this.getInputIndex();
    if (currentValue) {
      const newValue = currentValue.slice(0, -1);
      currentInput['value'] = newValue;
      this.inputs[this.currentBoxInput] = newValue;
      this.setFocus(this.currentBoxInput);
    } else {
      const prevId = index - 1;
      const prevInput = `input${prevId}`;
      const prevBoxInput = document.getElementById(prevInput);
      if (prevBoxInput) {
        const prevValue = prevBoxInput['value'];
        const newValue = prevValue.slice(0, -1);
        this.setFocus(prevInput);
        prevBoxInput['value'] = newValue;
        this.inputs[prevInput] = newValue;
      }
    }
  }

  getInputIndex(): number {
    return this.currentBoxInput.length > 6
      ? parseInt(this.currentBoxInput.slice(-2))
      : parseInt(this.currentBoxInput.slice(-1));
  }

  private isInputLengthValid(): boolean {
    return this.code.length >= this.minLength && this.code.length <= this.maxLength;
  }

  private isBoxesLengthValid(): boolean {
    return this.code.length === this.maxLength;
  }

  preventConfirm(): boolean {
    const input = document.getElementById('code');
    const boxInput = document.getElementById('input0');
    this.code = input ? input['value'] : this.getInputBoxesValues();

    const shouldValidateCodeLength = [ORDER_ID_TYPE, SCAN_TYPE].includes(this.type);

    if (shouldValidateCodeLength) {
      if (input && !this.isInputLengthValid()) {
        this.showLengthRestrictionInputMsg();
        return true;
      }

      if (boxInput && !this.isBoxesLengthValid()) {
        this.showLengthRestrictionBoxesMsg();
        return true;
      }
    } else if (this.countryDialCode && this.code.length < this.maxLength) {
      return true;
    }

    return false;
  }

  confirm(): void {
    if (this.disableEnter) return;
    if (this.preventSubmit?.()) return;
    if (this.preventConfirm()) return;
    this.initInputBoxesValues();
    this.preparePhoneNumber();
    if (this.isPassword) {
      this.codeSubmitted.emit(this.code);
      return;
    }
    if (this.valueSubmit.observed) {
      this.valueSubmit.emit(this.code);
      return;
    }
    submitAccessCode(
      this.code,
      {
        type: this.type,
        flow: this.flow,
        side: this.side,
        viewType: this.viewType,
        expireLoaded: this.expireLoaded,
        enableDeliveryProvider: this.enableDeliveryProvider,
        initTransactionByCode: this.isRetailInitTransactionByCode(),
      },
      this.orderProcessingService,
      this.utils
    );
  }

  private isRetailInitTransactionByCode(): boolean {
    return (
      this.viewType === VIEW_TYPES.RETAIL && this.state.url.includes(RETAIL_PATHS.ENTER_SCAN_CODE)
    );
  }

  private preparePhoneNumber(): void {
    if (this.countryDialCode) {
      this.code = this.phoneNumberService.getFullPhoneNumber(
        this.code,
        this.countryDialCode,
        this.countryIsoCode
      );
    }
  }

  addPhysicalNumericKeyboardListener(): void {
    this.keydownHandler = (event: KeyboardEvent) => {
      event.preventDefault();
      const key = event.key;
      const isNumber = /^[0-9]+$/.test(key);
      if (isNumber) {
        this.setValueFromExternalKeyboard(event);
      }
      if (key === 'Enter') this.confirm();
    };
    document.addEventListener('keydown', this.keydownHandler);
  }

  setValueFromExternalKeyboard(event: KeyboardEvent): void {
    const id = event.target['id'];
    if (id && !id.includes('select')) {
      this.currentBoxInput = id;
      event.target['textContent'] = event.key;
      this.setValue(event);
    }
  }

  getInputBoxesValues(): string {
    const values = Object.values(this.inputs);
    return values.join('');
  }

  focusNext(index: number): void {
    const currentInput = document.getElementById(this.currentBoxInput);
    const currentValue = currentInput['value'];
    const nextId = index + 1;
    if (currentValue && nextId < this.maxLength) {
      this.setFocus(`input${nextId}`);
    }
  }

  ngOnDestroy(): void {
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = undefined;
    }
    this.subscription.unsubscribe();
  }
}
